const cities = [
  { name: "Fort Myers", zips: "33901" },
  { name: "Cape Coral", zips: "33904" },
  { name: "Sanibel", zips: "33957" },
  { name: "Fort Myers Beach", zips: "33931" },
  { name: "Lehigh Acres", zips: "33936" },
  { name: "Bonita Springs", zips: "34135" },
  { name: "Estero", zips: "33928" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your home, office, or the River District in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function FortMyersLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Fort Myers &amp; Lee County
          </h2>
        </div>

        {/* 300–500 word intro block */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Fort Myers, Florida is a city built on discovery — from Thomas Edison and Henry Ford wintering here to modern families discovering Sanibel's shell-strewn beaches and the Caloosahatchee River's eco-tours. This is a community where spring training baseball draws crowds from across the Midwest, where retirees flock for affordable waterfront living compared to Naples just south, and where families plan Caribbean cruises, Canadian fishing trips, and Central American eco-adventures with the same enthusiasm locals bring to a JetBlue Park Red Sox game. That travel culture creates a very specific passport challenge: when a Fort Myers resident discovers an expired or damaged passport, the timeline is almost always shorter than standard government processing allows.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The family vacation dynamic is particularly strong here. Fort Myers, Cape Coral, and Lehigh Acres are filled with multi-generational families who plan group cruises to the Bahamas, spring break trips to Mexico, and summer adventures in Costa Rica. These trips are often booked during promotional windows — 45 to 60 days out — and then someone realizes a child's passport expired, a parent's name does not match their ticket after a recent divorce and remarriage, or a grandparent's passport was water-damaged during a boating trip on the river. Standard post office processing, which currently stretches well beyond three months, simply does not accommodate that reality.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Spring training season adds another unique urgency. Every February and March, thousands of Red Sox and Twins fans fly in from Boston, Minnesota, and across the Northeast — many with dual-residency ties to Fort Myers. Snowbirds who maintain homes here often discover passport issues right before their annual trip back north, or before joining a spring training road trip to Toronto or Montreal. The compressed timeline between booking flights and discovering document problems is a pattern we see repeatedly from Lee County clients.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Fast Passport Center was built for exactly this type of traveler. We provide the professional, expedited passport service that Fort Myers families, retirees, and snowbirds need: direct communication with a dedicated agent, professional document review before anything is submitted, and processing as fast as 24 hours for true emergencies. No lines. No guesswork. Just reliable service at a fair price — backed by our best price guarantee.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Info + How It Works */}
          <div>
            {/* FPC Info Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Nearest Office</p>
                  <p className="text-white/60 text-xs mt-0.5">1925 Metropolitan Blvd, Naples, FL 34112</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Fort Myers — approximately 40 miles south on I-75
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Lee County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our Naples office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Lee County — start online, ship overnight, and we handle the rest. No need to drive to Miami or Tampa.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Naples", href: "/cities/naples" },
                    { name: "Cape Coral", href: "/cities/cape-coral" },
                    { name: "Bonita Springs", href: "/cities/bonita-springs" },
                    { name: "Estero", href: "/cities/estero" },
                    { name: "Sarasota", href: "/cities/sarasota" },
                    { name: "Tampa", href: "/cities/tampa" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Fort Myers Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}